import {
  NEUTRAL_POSE,
  POSE_KEYS,
  type Animation,
  type AnimationTrack,
  type Channel,
  type CharacterPose,
  type Easing,
  type LoopMode,
  type PoseKey,
  type Scene,
  type SceneObject,
} from '../domain/schemas';

/**
 * Pure keyframe evaluation (Phase 3B). Nothing here depends on PixiJS, the DOM, the clock
 * or on previously evaluated frames: the same animation data and frame number always
 * give the same values.
 *
 * Timebase: integer frames `0 … durationFrames − 1` at `fps`. Seconds = frame / fps.
 *
 * Interpolation: a keyframe's `easing` shapes the segment from it to the next keyframe.
 * Every easing maps t ∈ [0,1] to [0,1], so an interpolated value always lies between its
 * two keyframes (it can never leave a channel's valid range). Before the first keyframe
 * its value holds; after the last keyframe the last value holds.
 *
 * Rotation is interpolated numerically with no wraparound: 350° → 10° turns 340° back
 * through 180°, and 0° → 720° spins twice. Key 360·n to spin.
 */

export function ease(easing: Easing, t: number): number {
  const u = Math.min(1, Math.max(0, t));
  switch (easing) {
    case 'step':
      return 0;
    case 'linear':
      return u;
    case 'ease-in':
      return u * u * u;
    case 'ease-out':
      return 1 - (1 - u) ** 3;
    case 'ease-in-out':
      return u < 0.5 ? 4 * u * u * u : 1 - (-2 * u + 2) ** 3 / 2;
  }
}

/** Value of a track at an integer (or fractional) frame; undefined for an empty track. */
export function trackValueAt(track: AnimationTrack, frame: number): number | undefined {
  const keys = track.keyframes;
  if (keys.length === 0) return undefined;
  if (frame <= keys[0]!.frame) return keys[0]!.value;
  const last = keys[keys.length - 1]!;
  if (frame >= last.frame) return last.value;
  // Binary search for the segment [a, b] with a.frame <= frame < b.frame.
  let lo = 0;
  let hi = keys.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (keys[mid]!.frame <= frame) lo = mid;
    else hi = mid;
  }
  const a = keys[lo]!;
  const b = keys[hi]!;
  if (track.channel === 'visible') return a.value;
  const t = (frame - a.frame) / (b.frame - a.frame);
  return a.value + (b.value - a.value) * ease(a.easing, t);
}

/** Channel values of one object at a frame (only animated channels are present). */
export type ChannelValues = Partial<Record<Channel, number>>;
export type EvaluatedState = ReadonlyMap<string, ChannelValues>;

/** Evaluates every track of an animation at a frame (clamped to the animation). */
export function evaluateAnimation(animation: Animation, frame: number): Map<string, ChannelValues> {
  const f = Math.min(Math.max(0, frame), animation.durationFrames - 1);
  const out = new Map<string, ChannelValues>();
  for (const track of animation.tracks) {
    const v = trackValueAt(track, f);
    if (v === undefined) continue;
    let values = out.get(track.objectId);
    if (!values) out.set(track.objectId, (values = {}));
    values[track.channel] = v;
  }
  return out;
}

// ------------------------------------------------------------------ Time ↔ frames

export const frameToSeconds = (frame: number, fps: number): number => frame / fps;
export const secondsToFrames = (seconds: number, fps: number): number => Math.round(seconds * fps);
export const durationSeconds = (a: { durationFrames: number; fps: number }): number => a.durationFrames / a.fps;

/**
 * Maps playback time (seconds since frame 0) to the frame to display. The frame is
 * derived from time, never counted, so dropped or late callbacks skip frames instead of
 * slowing the animation down. `ended` is true when a non-looping animation has reached
 * its last frame.
 */
export function frameAtTime(seconds: number, a: { durationFrames: number; fps: number; loop: LoopMode }): { frame: number; ended: boolean } {
  const n = a.durationFrames;
  // A tiny epsilon keeps exact frame boundaries (e.g. 1/24 s) from flooring one frame low.
  const raw = Math.floor(Math.max(0, seconds) * a.fps + 1e-6);
  if (n <= 1) return { frame: 0, ended: a.loop === 'once' && raw >= 1 };
  switch (a.loop) {
    case 'once':
      // The last frame is shown for its full frame duration, then playback ends.
      return raw >= n - 1 ? { frame: n - 1, ended: raw >= n } : { frame: raw, ended: false };
    case 'loop':
      return { frame: raw % n, ended: false };
    case 'pingpong': {
      const period = 2 * (n - 1);
      const p = raw % period;
      return { frame: p < n ? p : period - p, ended: false };
    }
  }
}

// ------------------------------------------------------------------ Displayed geometry

/** A scene object as drawn at the playhead: animated values applied to the base object. */
export type DisplayObject = SceneObject & { pose?: CharacterPose };

export function poseOf(o: SceneObject): CharacterPose {
  return o.type === 'character' && o.pose ? o.pose : NEUTRAL_POSE;
}

/** Base (un-animated) value of a channel for an object. */
export function baseValue(o: SceneObject, channel: Channel): number {
  switch (channel) {
    case 'x':
      return o.x;
    case 'y':
      return o.y;
    case 'rotation':
      return o.rotation;
    case 'scaleX':
    case 'scaleY':
      return 1;
    case 'opacity':
      return o.opacity;
    case 'visible':
      return o.visible ? 1 : 0;
    default:
      return poseOf(o)[channel.slice(5) as PoseKey];
  }
}

/**
 * Applies animated values to a base object. Scale channels resize the box about its
 * anchor (the anchor point stays fixed), so the result is again a plain box that the
 * renderer, hit-testing and the selection overlay can use unchanged.
 */
export function displayObject(o: SceneObject, values: ChannelValues | undefined): DisplayObject {
  if (!values) return o;
  const sx = values.scaleX ?? 1;
  const sy = values.scaleY ?? 1;
  const x = values.x ?? o.x;
  const y = values.y ?? o.y;
  const width = o.width * sx;
  const height = o.height * sy;
  const out = {
    ...o,
    x: x + o.anchorX * o.width * (1 - sx),
    y: y + o.anchorY * o.height * (1 - sy),
    width,
    height,
    rotation: values.rotation ?? o.rotation,
    opacity: values.opacity ?? o.opacity,
    visible: values.visible === undefined ? o.visible : values.visible >= 0.5,
  } as DisplayObject;
  if (o.type === 'character') {
    let pose: CharacterPose | null = null;
    for (const k of POSE_KEYS) {
      const v = values[`pose.${k}` as Channel];
      if (v === undefined) continue;
      pose ??= { ...poseOf(o) };
      pose[k] = v;
    }
    if (pose) (out as { pose?: CharacterPose }).pose = pose;
  }
  return out;
}

/** The scene as displayed at the playhead (objects replaced by their displayed geometry). */
export function displayScene(scene: Scene, evaluated: EvaluatedState | null): Scene {
  if (!evaluated || evaluated.size === 0) return scene;
  return { ...scene, objects: scene.objects.map((o) => displayObject(o, evaluated.get(o.id))) };
}

/**
 * Converts a wanted displayed box back to channel values (the inverse of
 * `displayObject` for x, y, scaleX and scaleY). Used by Auto-Key for drags and resizes.
 */
export function channelsForBox(base: SceneObject, box: { x: number; y: number; width: number; height: number }): { x: number; y: number; scaleX: number; scaleY: number } {
  const scaleX = box.width / base.width;
  const scaleY = box.height / base.height;
  return {
    scaleX,
    scaleY,
    x: box.x - base.anchorX * base.width * (1 - scaleX),
    y: box.y - base.anchorY * base.height * (1 - scaleY),
  };
}
